import { configureStore } from "@/lib/store"

import { adminApiService } from "../services/admin-api-service"
import type { AdminResource, AuthOrganization, AuthUser, BreadcrumbItem } from "../types"

interface AdminState {
  // Recursos descobertos
  resources: Record<string, AdminResource>
  resourcesLoading: boolean

  // Contexto atual
  currentUser: AuthUser | null
  currentOrganization: AuthOrganization | null
  activeTab: string

  // Filtros globais
  globalFilters: Record<string, any>

  // Navegação
  breadcrumbs: BreadcrumbItem[]

  // Actions
  setResources: (resources: Record<string, AdminResource>) => void
  setCurrentContext: (user: AuthUser | null, organization: AuthOrganization | null) => void
  setActiveTab: (tab: string) => void
  setGlobalFilters: (filters: Record<string, any>) => void
  setBreadcrumbs: (breadcrumbs: BreadcrumbItem[]) => void
  clearContext: () => void

  // API Actions
  fetchResources: () => Promise<void>
}

export const useAdminStore = configureStore<AdminState>(
  set => ({
    // Initial state
    resources: {},
    resourcesLoading: false,
    currentUser: null,
    currentOrganization: null,
    activeTab: "overview",
    globalFilters: {},
    breadcrumbs: [],

    // Sync actions
    setResources: resources => set({ resources }),

    setCurrentContext: (user, organization) =>
      set({
        currentUser: user,
        currentOrganization: organization,
        activeTab: "overview", // Reset tab when context changes
      }),

    setActiveTab: tab => set({ activeTab: tab }),

    setGlobalFilters: filters => set({ globalFilters: filters }),

    setBreadcrumbs: breadcrumbs => set({ breadcrumbs }),

    clearContext: () =>
      set({
        currentUser: null,
        currentOrganization: null,
        activeTab: "overview",
      }),

    // Async actions
    fetchResources: async () => {
      set({ resourcesLoading: true })
      try {
        const resources = await adminApiService.getResources()
        set({ resources, resourcesLoading: false })
      } catch (error) {
        console.error("Failed to fetch admin resources:", error)
        set({ resourcesLoading: false })
      }
    },
  }),
  {
    persistOptions: {
      name: "admin-store",
    },
  }
)
